import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useDuckDB } from '../context/LazyDuckDBContext';

interface CoverageMiniCardProps {
  kpiId: string;
  currentYear: number;
  muni: string;
  compareScope?: 'municipality' | 'national';
}

interface SchoolStats {
  schools_with_kpi: number;
  number_of_school_with_missing_kpi: number;
}

const CoverageMiniCard: React.FC<CoverageMiniCardProps> = ({ kpiId, currentYear, muni, compareScope = 'municipality' }) => {
  const { query } = useDuckDB();
  const [stats, setStats] = useState<SchoolStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      if (!kpiId || !currentYear || !muni) return;

      try {
        setLoading(true);
        setError(null);

        const sql = `
          SELECT 
            COUNT(DISTINCT entity_id) - COUNT(DISTINCT CASE WHEN status='Missing' THEN entity_id ELSE NULL END) as schools_with_kpi,
            COUNT(DISTINCT CASE WHEN status='Missing' THEN entity_id ELSE NULL END) as number_of_school_with_missing_kpi
          FROM gold_kpi_trend 
          WHERE ${compareScope === 'municipality' ? `municipality_id='${muni}'` : 'TRUE'}
            AND kpi_id='${kpiId}' 
            AND "year"=${currentYear} 
            AND gender='T' 
            AND entity_type='org_unit'
        `;

        const result = await query<SchoolStats[]>(sql);
        
        if (result && result.length > 0) {
          setStats(result[0]);
        }
      } catch (err) {
        console.error('Error fetching school stats:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [kpiId, currentYear, muni, query, compareScope]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleShowMissingSchools = () => {
    // TODO: Implement modal to show schools without data
  };

  if (loading) {
    return (
      <section className="rounded-lg bg-card ring-1 ring-border p-3">
        <div className="text-[11px] font-medium text-foreground/80 mb-2">Datatäckning</div>
        <div className="flex items-center justify-center h-20">
          <div className="animate-pulse text-muted-foreground text-xs">Laddar...</div>
        </div>
      </section>
    );
  }

  if (error || !stats) {
    return (
      <section className="rounded-lg bg-card ring-1 ring-border p-3">
        <div className="text-[11px] font-medium text-foreground/80 mb-2">Datatäckning</div>
        <div className="text-center text-danger text-xs">
          {error ? 'Fel vid laddning' : 'Ingen data'}
        </div>
      </section>
    );
  }

  const total = stats.schools_with_kpi + stats.number_of_school_with_missing_kpi;
  const withData = stats.schools_with_kpi;
  const missing = stats.number_of_school_with_missing_kpi;
  const pct = total ? Math.round((withData / total) * 100) : 0;
  const size = 112;

  const data = [
    { k: "present", name: "Med data", value: withData },
    { k: "missing", name: "Saknar", value: missing },
  ];
  const C = { present: "hsl(var(--grid))", missing: "hsl(var(--warning))" };

  return (
    <section
      className="rounded-lg bg-card ring-1 ring-border p-3"
      aria-label={`Datatäckning ${pct}% av ${total} skolor`}
    >
      <div className="text-[11px] font-medium text-foreground/80 mb-2">
        Datatäckning
      </div>

      <div className="flex items-center gap-3">
        <div className="relative shrink-0" style={{ width: size, height: size }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                innerRadius={size/2 - 12}
                outerRadius={size/2}
                stroke="hsl(var(--card))"
                strokeWidth={2}
                isAnimationActive={false}
                paddingAngle={0}
              >
                {data.map(d => (
                  <Cell key={d.k} fill={d.k === "missing" ? C.missing : C.present} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-center leading-tight">
              <div className="text-xl font-semibold text-foreground">{pct}%</div>
              <div className="text-[10px] text-muted-foreground">täckning</div>
            </div>
          </div>
        </div>

        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">
            Totalt antal skolor <span className="font-medium text-foreground">{total}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="inline-flex items-center gap-1">
              <i className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--warning))]" /> Saknar {missing}
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <i className="h-2.5 w-2.5 rounded-full bg-[hsl(var(--grid))]" /> Med data {withData}
            </span>
          </div>
          {/* {missing > 0 && (
            <button 
              className="mt-1 text-xs text-primary hover:opacity-90 underline"
              onClick={handleShowMissingSchools}
            >
              Visa {missing} skolor utan data
            </button>
          )} */}
        </div>
      </div>
    </section>
  );
};

export default CoverageMiniCard;
